import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';

const AgencyContext = createContext(null);

// Izabrana agencija se pamti samo dok je tab otvoren (sessionStorage),
// pa se pri svakom novom otvaranju aplikacije prvo prikaže lista agencija.
// Osvežavanje stranice u istom tabu zadržava izbor.
const STORAGE_KEY = 'finart_active_agency_id';
const store = window.sessionStorage;

const CURRENT_YEAR = new Date().getFullYear();

// Administrator aplikacije (vidi uvoz naloga iz Excela i slične alate)
export const ADMIN_EMAILS = ['sandra.joksimovic@fvu.me'];
export const yearOf = (d) => Number(String(d || '').slice(0, 4));

export function AgencyProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [agencies, setAgencies] = useState([]);
  const [activeAgencyId, setActiveAgencyId] = useState(() => store.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(true);
  // Firme nisu mogle da se učitaju (npr. loš internet) — tada NE znači da korisnik nema firmu
  const [loadError, setLoadError] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Izabrana godina — pri svakom ulasku i promeni agencije kreće od tekuće
  const [year, setYear] = useState(CURRENT_YEAR);
  const [years, setYears] = useState([CURRENT_YEAR]);

  // Za kog korisnika su agencije već učitane — da se "učitavanje"
  // (koje sakriva stranicu) prikaže samo prvi put, a ne pri svakom osvežavanju.
  const loadedForUser = useRef(null);

  const loadAgencies = useCallback(async () => {
    if (!userId) {
      loadedForUser.current = null;
      setAgencies([]);
      setLoading(false);
      return;
    }
    const firstLoad = loadedForUser.current !== userId;
    if (firstLoad) setLoading(true);
    const { data, error } = await supabase
      .from('agencies')
      .select('*')
      .eq('owner_user_id', userId)
      .order('created_at', { ascending: true });
    // Greška pri učitavanju: zadrži ono što je već prikazano i ne nudi pravljenje nove firme
    if (error) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    setLoadError(false);
    setAgencies(data || []);
    loadedForUser.current = userId;
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    loadAgencies();
  }, [loadAgencies]);

  // Korisnik sa samo jednom firmom ulazi direktno u nju (jednom po otvaranju taba).
  // Posle "Promeni nalog" vidi listu, da bi mogao da doda novu firmu.
  const autoPicked = useRef(null);
  useEffect(() => {
    if (loading || !userId || loadedForUser.current !== userId || autoPicked.current === userId) return;
    autoPicked.current = userId;
    if (!activeAgencyId && agencies.length === 1) {
      setActiveAgencyId(agencies[0].id);
      store.setItem(STORAGE_KEY, agencies[0].id);
    }
  }, [loading, agencies, activeAgencyId, userId]);

  // Ako je sačuvani izbor nevažeći (npr. obrisana agencija), očisti ga
  useEffect(() => {
    if (!loading && activeAgencyId && agencies.length > 0) {
      const stillExists = agencies.some((a) => a.id === activeAgencyId);
      if (!stillExists) {
        setActiveAgencyId(null);
        store.removeItem(STORAGE_KEY);
      }
    }
  }, [loading, agencies, activeAgencyId]);

  const loadProfile = useCallback(async (agencyId) => {
    if (!agencyId) {
      setProfile(null);
      return;
    }
    setProfileLoading(true);
    const { data } = await supabase
      .from('agency_profile')
      .select('*')
      .eq('agency_id', agencyId)
      .maybeSingle();
    setProfile(data || null);
    setProfileLoading(false);
  }, []);

  // Godine u kojima agencija ima dokumente (+ tekuća godina uvek)
  const loadYears = useCallback(async (agencyId) => {
    if (!agencyId) {
      setYears([CURRENT_YEAR]);
      return;
    }
    const { data } = await supabase.from('invoices').select('issue_date').eq('agency_id', agencyId);
    const set = new Set([CURRENT_YEAR]);
    (data || []).forEach((r) => { const y = yearOf(r.issue_date); if (y) set.add(y); });
    setYears([...set].sort((a, b) => b - a));
  }, []);

  useEffect(() => {
    setProfile(null); // nova agencija — ne prikazuj podatke prethodne
    setYear(CURRENT_YEAR);
    loadProfile(activeAgencyId);
    loadYears(activeAgencyId);
  }, [activeAgencyId, loadProfile, loadYears]);

  const refreshProfile = useCallback(() => {
    loadProfile(activeAgencyId);
  }, [activeAgencyId, loadProfile]);

  const refreshYears = useCallback(() => {
    loadYears(activeAgencyId);
  }, [activeAgencyId, loadYears]);

  const selectAgency = useCallback((agencyId) => {
    setActiveAgencyId(agencyId);
    if (agencyId) store.setItem(STORAGE_KEY, agencyId);
    else store.removeItem(STORAGE_KEY);
  }, []);

  const exitAgency = useCallback(() => {
    setActiveAgencyId(null);
    store.removeItem(STORAGE_KEY);
  }, []);

  const createAgency = useCallback(async (name) => {
    if (!userId) return null;
    const { data, error } = await supabase
      .from('agencies')
      .insert({ owner_user_id: userId, name })
      .select()
      .single();
    if (!error && data) {
      setAgencies((prev) => [...prev, data]);
      return data;
    }
    return null;
  }, [userId]);

  const activeAgency = agencies.find((a) => a.id === activeAgencyId) || null;
  const isAdmin = ADMIN_EMAILS.includes(String(user?.email || '').toLowerCase());

  // Da li korisnik sme da ima više firmi (tabela user_permissions; podešava administrator)
  const [canAddMore, setCanAddMore] = useState(false);
  useEffect(() => {
    if (!userId) { setCanAddMore(false); return; }
    let cancelled = false;
    supabase.from('user_permissions').select('can_add_agencies').eq('user_id', userId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setCanAddMore(Boolean(data?.can_add_agencies)); });
    return () => { cancelled = true; };
  }, [userId]);
  // Prvu firmu može svako; svaku sledeću samo administrator ili korisnik sa dozvolom
  const canAddAgency = isAdmin || canAddMore || (agencies.length === 0 && !loadError);

  return (
    <AgencyContext.Provider
      value={{
        agencies, activeAgency, activeAgencyId, loading, loadError, selectAgency, exitAgency, createAgency,
        refreshAgencies: loadAgencies, profile, profileLoading, refreshProfile,
        year, setYear, years, refreshYears, currentYear: CURRENT_YEAR, isAdmin, canAddAgency,
      }}
    >
      {children}
    </AgencyContext.Provider>
  );
}

export function useAgency() {
  return useContext(AgencyContext);
}
